import { type ComponentType, createElement, type Key, type ReactElement } from "react";
import type { PeekEntity } from "@/components/shared/relationships/entity-peek-context";

/** Content component a feature slice supplies for one quick-view type. */
export type PeekContentComponent = ComponentType<{ id: string }>;

/**
 * Slice-owned quick-view registration.
 *
 * Each feature slice owns its type's content renderer and data hook and
 * registers them here from its own `<Type>PeekContent` module. Those modules
 * are loaded once at the app root via `src/app/peek-registrations.ts` — the
 * single registration mechanism — so every `EntityPeekProvider` mount point
 * sees the full type set before anything can open a peek. The shared host
 * renders content through `renderPeekContent` and never imports
 * `src/features/`; a type nothing has registered resolves to `undefined` and
 * the host renders its inline error state with retry instead of crashing.
 */
const peekContentByType = new Map<PeekEntity["type"], PeekContentComponent>();

/** Registers (or replaces) the content component for a quick-view type. */
export function registerPeekType(type: PeekEntity["type"], content: PeekContentComponent): void {
  peekContentByType.set(type, content);
}

/** Creates an element of the registered, stable component type without calling it. */
export function renderPeekContent(
  type: PeekEntity["type"],
  id: string,
  key: Key,
): ReactElement | undefined {
  const content = peekContentByType.get(type);
  return content ? createElement(content, { id, key }) : undefined;
}
